import { useContext, useMemo } from 'react'
import { shallowEqual, useAppSelector } from 'hooks/reduxHooks'
import { getAllOffers } from 'selectors/offerSelectors'
import { arrayToMap } from 'lib/array/arrayUtils'
import { isCartPanelAccommodationUnavailableStatus, resolveItemPrice } from '../utils/cartPanelUtils'
import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import getLEPackageItemTotals from 'checkout/lib/utils/accommodation/getLEPackageItemTotals'
import { getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import { isMaldivesCharterRegionEnabled } from 'selectors/maldivesSelectors'
import { buildCharterCartFlightPriceInput, useCharterCartFlightPrices, type CharterCartFlightPriceInput } from './useCharterCartFlightPrices'
import type { CartPanelAccommodationPricingInput } from '../utils/tripChatCartPricingInputUtils'

export type PricingInput = CartPanelAccommodationPricingInput

export interface ResolvedItemPricing {
  price: number | undefined
  memberPrice: number | undefined
  value: number | undefined
  isAvailable: boolean | undefined
  loading: boolean
  error: boolean
  offer: App.HotelOffer | App.BedbankOfferSummary | undefined
}

export function useItineraryItemPricing(
  items: Array<PricingInput>,
): Map<number, ResolvedItemPricing> {
  const { offers, offersLoading, offerErrors, calendarsByOccupancy, availableRatesByOccupancy, email } = useAppSelector(state => ({
    offers: getAllOffers(state),
    offersLoading: state.offer.offersLoading,
    offerErrors: state.offer.offerErrors,
    calendarsByOccupancy: state.calendar.calendarsByOccupancy,
    availableRatesByOccupancy: state.offer.offerAvailableRatesByOccupancy,
    email: state.auth?.account?.email,
  }), shallowEqual)
  const { currentRegionCode } = useContext(GeoContext)
  const currency = useContext(CurrencyContext)
  const isMaldivesCharterEnabled = useAppSelector(isMaldivesCharterRegionEnabled)

  // Live charter-bundle flight price per charter item — added on top of the
  // hotel price so the cart/total reflect hotel + flight (like the offer page).
  const charterInputs = useMemo<Array<CharterCartFlightPriceInput>>(
    () => items.flatMap((item) => {
      if (isCartPanelAccommodationUnavailableStatus(item.cartStatus)) return []
      const input = buildCharterCartFlightPriceInput({
        tripItemId: item.tripItemId,
        offer: offers[item.offerId],
        isMaldivesCharterEnabled,
        charterFlightBundle: item.charterFlightBundle,
        checkIn: item.checkIn,
        checkOut: item.checkOut,
        occupancies: item.occupancies?.length ? item.occupancies : (item.occupancy ? [item.occupancy] : []),
        region: currentRegionCode,
        currency,
        email,
      })
      return input ? [input] : []
    }),
    [items, offers, isMaldivesCharterEnabled, currentRegionCode, currency, email],
  )
  const charterFlightPrices = useCharterCartFlightPrices(charterInputs)

  return useMemo(() => {
    const pricingMap = arrayToMap(items, item => item.tripItemId, item => {
      const rawOffer = offers[item.offerId]
      const hotelOffer = rawOffer && isLEHotel(rawOffer) ? rawOffer : undefined
      const bedbankOffer = rawOffer && isBedbankOffer(rawOffer) ? rawOffer : undefined
      const offer = hotelOffer ?? bedbankOffer
      const loading = !!offersLoading[item.offerId]
      const error = !!offerErrors[item.offerId]

      if (isCartPanelAccommodationUnavailableStatus(item.cartStatus)) {
        return {
          price: item.savedPrice,
          memberPrice: item.savedMemberPrice,
          value: item.savedValue,
          isAvailable: false,
          loading: false,
          error: false,
          offer,
        }
      }

      if (hotelOffer && item.packageId && item.checkIn && item.checkOut && item.occupancy) {
        const uniqueKey = getPackageUniqueKey(item.packageId, item.duration ?? 0, item.roomRateId ?? '')
        const pkg = hotelOffer.packages.find(p => p.uniqueKey === uniqueKey)

        if (pkg) {
          const itemTotals = getLEPackageItemTotals(
            hotelOffer,
            pkg,
            item.checkIn,
            item.checkOut,
            item.occupancy,
            undefined,
            calendarsByOccupancy,
            availableRatesByOccupancy,
          )

          if (itemTotals) {
            const displayTotals = getUserCartItemDisplayTotals(itemTotals)
            return {
              price: displayTotals.publicTotal,
              memberPrice: displayTotals.memberTotal,
              value: itemTotals.value,
              isAvailable: itemTotals.isAvailable,
              loading,
              error,
              offer: hotelOffer,
            }
          }
        }
      }

      if (item.isBedbankHotel || bedbankOffer) {
        return {
          price: item.cartPrice ?? item.savedPrice,
          memberPrice: item.cartMemberPrice ?? item.savedMemberPrice,
          value: item.savedValue,
          isAvailable: undefined,
          loading: false,
          error,
          offer: bedbankOffer,
        }
      }

      const resolved = resolveItemPrice(hotelOffer, item.packageId)

      return {
        price: resolved.price ?? item.cartPrice ?? item.savedPrice,
        memberPrice: resolved.memberPrice ?? item.cartMemberPrice ?? item.savedMemberPrice,
        value: resolved.value ?? item.savedValue,
        isAvailable: undefined,
        loading: loading || (!offer && !error),
        error,
        offer,
      }
    })

    // Add the live charter flight price on top of the hotel price (new object per
    // item, no mutation); mark unavailable when the flight can't be priced.
    const withCharterPricing = new Map<number, ResolvedItemPricing>()
    for (const [tripItemId, pricing] of pricingMap) {
      const charter = charterFlightPrices.get(tripItemId)
      if (!charter) {
        withCharterPricing.set(tripItemId, pricing)
        continue
      }
      const flightPrice = charter.flightPrice
      withCharterPricing.set(tripItemId, {
        ...pricing,
        price: flightPrice !== undefined && pricing.price !== undefined ? pricing.price + flightPrice : pricing.price,
        memberPrice: flightPrice !== undefined && pricing.memberPrice !== undefined ? pricing.memberPrice + flightPrice : pricing.memberPrice,
        value: flightPrice !== undefined && pricing.value !== undefined ? pricing.value + flightPrice : pricing.value,
        isAvailable: flightPrice === undefined && charter.unavailable ? false : pricing.isAvailable,
        loading: pricing.loading || charter.loading,
      })
    }

    return withCharterPricing
  }, [items, offers, offersLoading, offerErrors, calendarsByOccupancy, availableRatesByOccupancy, charterFlightPrices])
}
